// Feast for Crows' "Name a spell" prompt (server promptKind 'name_spell').
// The server's modal choices span the whole spell catalog (~850 cards), so
// the prompt renders through the searchable PileSearchDialog over a synthetic
// pile (the Dragonlord set-aside pattern), never the generic button modal.
// The pick replies with the chosen catalog id via chooseIntent; the server
// banishes the opponent's matching copies and shuffles their spellbook. The
// prompt is MANDATORY: closing the dialog sends nothing (the server keeps the
// prompt open) and the next mirror fold re-opens the picker.
import { chooseIntent } from '../../../utils/game/net/automationIntents';
import { t } from '../../../localization/t';

export function foldNameSpellPromptFromMirror(host, pc) {
  const isNamePick = !!pc && pc.promptKind === 'name_spell';
  if (!isNamePick) {
    // The tracked prompt resolved server-side. Close a picker still up.
    if (host.state.pendingNameSpellPick?.promptId) {
      host.setState({ pendingNameSpellPick: null, searchPile: null, searchQuery: '' });
    }
    return false;
  }
  // Solo: the pilot answers both seats; online only the addressee picks (the
  // opponent just sees the resulting banishes fold in).
  if (!host.props?.isCasualSandbox && pc.seat !== host._localSeat?.()) return true;
  // Mandatory prompt: an ESC'd dialog clears only the LOCAL picker state, so
  // re-open whenever the dialog is down (mirrors the pile-prompt re-attempt).
  if (host.state.pendingNameSpellPick?.promptId === pc.promptId && host.state.searchPile) return true;

  // Synthetic pile. Display-only card entries the PileSearchDialog can render
  // (it resolves art via cardId + printingId against the catalog). Clicks
  // route through _resolveNameSpellPick, never the take-to-hand mutations.
  const pool = (pc.choices || [])
    .map((c) => host._getFullCard(c.value))
    .filter(Boolean);
  const pile = {
    id: 'name-spell-pick',
    name: t('Name a Spell'),

    rotated: pc.seat === 'p2',
    cards: pool.map((full) => {
      const printing = full.printings?.[0] || {};
      return {
        id: `namepick-${full.unique_id}`,
        cardId: full.unique_id,
        name: full.name,
        imageUrl: printing.image_url || null,
        printingId: printing.unique_id || null,
        foiling: printing.foiling || null,
      };
    }),
  };
  host.setState({
    searchPile: pile,
    searchQuery: '',
    pendingNameSpellPick: { promptId: pc.promptId, seat: pc.seat },
  });
  return true;
}

// The pick replies to the server prompt. The name_spell_banish resume banishes
// the opponent's matching copies (public. Both seats see exactly what the
// search found) and shuffles their spellbook.
export function resolveNameSpellPick(host, pickedCard) {
  const pending = host.state.pendingNameSpellPick;
  if (!pending?.promptId) return;
  const full = host._getFullCard(pickedCard.cardId);
  host._sendMatchIntent?.(chooseIntent(pending.promptId, pickedCard.cardId, pending.seat));
  host.logAction('Named {card}', { card: full?.name || pickedCard.name });
  host.setState({ pendingNameSpellPick: null, searchPile: null, searchQuery: '' });
}
